import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronRight, ClipboardCheck, Loader, RefreshCw } from 'lucide-react';
import { ChartReviewService, ReviewQueue } from '../../services/patientmr/chartReview.service';
import type { ChartReviewItem } from '../../dtos/patientmr/chartReview.dto';
import { DocStatusPill } from '../common/Pills';
import { describeApiError } from '../../utils/apiErrors';
import { openPatientChartById } from '../../constants/navigationEvents';

const waited = (h: number | null): string => {
  if (h === null || h === undefined) return '';
  if (h < 1) return 'under 1 h';
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
};

const visitLabel = (i: ChartReviewItem): string => {
  const d = i.startTime ? new Date(i.startTime) : null;
  const date = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString([], { month: 'short', day: 'numeric' }) : (i.visitDate || '');
  const t = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
  const window = i.startTime ? `${t(i.startTime)}${i.endTime ? `–${t(i.endTime)}` : ''}` : '';
  return [date, window].filter(Boolean).join(' · ');
};

const Flags: React.FC<{ item: ChartReviewItem }> = ({ item }) => (
  <span className="flex flex-wrap gap-1">
    {item.maxIncidentSeverity && item.maxIncidentSeverity !== 'LOW' && (
      <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-800">{item.maxIncidentSeverity.toLowerCase()} incident</span>
    )}
    {item.reopenRequested && <span className="rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-xs font-semibold text-indigo-800">Reopen requested</span>}
    {item.returnedBefore && <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-900">Returned before</span>}
    {item.reopened && <span className="rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-xs font-semibold text-sky-900">Amended</span>}
  </span>
);

/**
 * Phase 18U U16: every chart awaiting review across patients (SUBMITTED or in QA), oldest
 * submission first. Opening one goes to that visit's chart in review; "Next chart" there walks
 * this list.
 */
const ReviewQueuePage: React.FC = () => {
  const [items, setItems] = useState<ChartReviewItem[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ChartReviewService.all();
      setItems(result.items);
      setTruncated(result.truncated);
      ReviewQueue.set(result.items);
    } catch (e) {
      setError(describeApiError(e, 'The charts awaiting review could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (item: ChartReviewItem) => openPatientChartById(item.patientId, 'Charting', item.visitId);

  return (
    <div className="mx-auto max-w-5xl px-4 py-4" data-testid="review-queue">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <ClipboardCheck size={22} aria-hidden="true" />
          Charts awaiting review{items ? ` (${items.length})` : ''}
        </h1>
        <button type="button" onClick={load} disabled={loading} className="flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 disabled:opacity-50">
          {loading ? <Loader size={16} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}Refresh
        </button>
      </div>
      <p className="mb-3 text-sm text-gray-700">Submitted and in-QA charts from the last 30 days, oldest submission first.</p>
      {error && (
        <div role="alert" className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle size={16} aria-hidden="true" />{error}
          <button type="button" onClick={load} className="ml-auto underline">Try again</button>
        </div>
      )}
      {truncated && <p className="mb-2 text-sm text-amber-900">Showing the first part of a long list. Narrow it by finishing the oldest charts first.</p>}
      {!items && !error && <p role="status" className="text-sm text-gray-600">Loading charts…</p>}
      {items && items.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">No charts are waiting for review.</p>}
      {items && items.length > 0 && (
        <>
          <div className="mb-2 hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_6rem_5rem_minmax(0,1.2fr)_1.5rem] gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 md:grid" aria-hidden="true">
            <span>Patient</span><span>Clinician</span><span>Visit</span><span>Chart</span><span>Waiting</span><span>Flags</span><span />
          </div>
          <ul className="space-y-1.5">
            {items.map((item) => (
              <li key={item.visitId}>
                <button type="button" onClick={() => open(item)} data-testid="review-row"
                  className="w-full rounded-lg border border-gray-200 bg-white text-left hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-600">
                  <span className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_6rem_5rem_minmax(0,1.2fr)_1.5rem] items-center gap-3 px-4 py-2 md:grid">
                    <span className="truncate text-sm font-medium text-gray-900">{item.patientName || 'Patient'}</span>
                    <span className="truncate text-sm text-gray-800">{item.providerName || '—'}{item.discipline ? ` · ${item.discipline}` : ''}</span>
                    <span className="truncate text-sm text-gray-700">{visitLabel(item)}</span>
                    <span><DocStatusPill status={item.chartingStatus} /></span>
                    <span className="text-sm text-gray-700">{waited(item.hoursSinceSubmit)}</span>
                    <Flags item={item} />
                    <ChevronRight size={16} className="text-gray-500" aria-hidden="true" />
                  </span>
                  <span className="flex min-h-[64px] items-center justify-between gap-2 p-3 md:hidden">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900">{item.patientName || 'Patient'}</span>
                      <span className="block truncate text-xs text-gray-600">{item.providerName || 'Clinician not recorded'} · {visitLabel(item)}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-1"><DocStatusPill status={item.chartingStatus} /><span className="text-xs text-gray-700">waiting {waited(item.hoursSinceSubmit)}</span><Flags item={item} /></span>
                    </span>
                    <ChevronRight size={18} className="shrink-0 text-gray-500" aria-hidden="true" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default ReviewQueuePage;
